// MediaPicker — dialog for picking image(s) from the Media Library.
//
// Two modes:
//   • Single-select (default) — used by the public EditImage modal. `onSelect`
//     fires once with the chosen asset.
//   • Multi-select — pass `multiple={true}` (used by /admin/galleries). The
//     user toggles assets via overlay checkboxes; `onSelect` fires once with
//     the full array of selected assets when they hit Confirm.
//
// Both modes read the public listing endpoints (no auth required).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Search, X, Folder, ChevronRight, ImageIcon, ArrowLeft,
  CheckSquare, Square,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || "";

// Picker thumbnails: route through Bunny when configured so the admin
// loads small derivatives (~30 KB) instead of full-size CMS originals
// (~1–2 MB). Falls back to the bare backend URL when the CDN flag is
// empty — same behaviour the picker had pre-Bunny.
//
// We deliberately pick WebP over AVIF for the picker grid: thumbnails
// are 180-220 px on screen so the visual quality is identical, but
// WebP encoding is 5-10× faster than AVIF on cold cache. Firing 20-30
// AVIF transforms simultaneously when the modal opens was exceeding
// Bunny Optimizer's per-zone transform budget and causing transient
// 504 Gateway Timeouts. WebP avoids that entirely.
const PICKER_THUMB_WIDTH = 240;
const resolveUrl = (u) => {
  if (!u) return "";
  if (!u.startsWith("/api/")) return u;
  if (CDN_BASE_URL) {
    const path = u.split("?")[0];
    return `${CDN_BASE_URL}${path}?width=${PICKER_THUMB_WIDTH}&format=webp&quality=72`;
  }
  return `${API}${u}`;
};

const parentOf = (path) => {
  if (!path || path === "/") return "/";
  const parts = path.split("/").filter(Boolean);
  parts.pop();
  return parts.length ? "/" + parts.join("/") : "/";
};

const buildTree = (folders) => {
  const byParent = new Map();
  byParent.set("/", []);
  folders.forEach((f) => {
    if (!byParent.has(f.parent)) byParent.set(f.parent, []);
    byParent.get(f.parent).push(f);
    if (!byParent.has(f.path)) byParent.set(f.path, []);
  });
  return byParent;
};

const MediaPicker = ({
  onSelect,
  onClose,
  multiple = false,
  title,
  confirmLabel,
}) => {
  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [path, setPath] = useState("/");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);          // single mode
  const [selectedMap, setSelectedMap] = useState(() => new Map()); // multi mode: id → asset

  const byParent = useMemo(() => buildTree(folders), [folders]);

  useEffect(() => {
    fetch(`${API}/api/media/folders`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setFolders(d.folders || []))
      .catch((e) => setError(e.message));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (filter.trim()) params.set("q", filter.trim());
    else params.set("folder", path);
    fetch(`${API}/api/media/assets?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setAssets(d.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filter, path]);

  useEffect(() => { load(); }, [load]);

  const crumbs = useMemo(() => {
    if (path === "/") return [{ label: "/", path: "/" }];
    const parts = path.split("/").filter(Boolean);
    const out = [{ label: "/", path: "/" }];
    let acc = "";
    for (const p of parts) {
      acc += "/" + p;
      out.push({ label: p, path: acc });
    }
    return out;
  }, [path]);

  const children = byParent.get(path) || [];
  const selected = useMemo(() => assets.find((a) => a.id === selectedId), [assets, selectedId]);

  const toggleMulti = useCallback((asset) => {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(asset.id)) next.delete(asset.id);
      else next.set(asset.id, asset);
      return next;
    });
  }, []);

  const clearMulti = useCallback(() => setSelectedMap(new Map()), []);

  const confirmSingle = useCallback(() => {
    if (onSelect && selected) onSelect(selected);
  }, [onSelect, selected]);

  const confirmMulti = useCallback(() => {
    if (!onSelect || !multiple) return;
    // Insertion order = roughly click order; preserves what the user picked.
    onSelect(Array.from(selectedMap.values()));
  }, [multiple, onSelect, selectedMap]);

  const heading = title || (multiple ? "Add from Media Library" : "Select from Media Library");
  const finalConfirmLabel = confirmLabel ||
    (multiple
      ? selectedMap.size > 0
        ? `Add ${selectedMap.size} image${selectedMap.size > 1 ? "s" : ""}`
        : "Add to gallery"
      : "Use this image");

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[130] bg-[#1A1513]/85"
      onClick={onClose}
      data-testid="media-picker-modal"
    >
      <div className="bg-white w-screen h-screen flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#2C2621]/10">
          <div>
            <p className="overline text-[#C16542]">{heading}</p>
            <p className="text-sm text-[#5C5248] mt-1">
              {multiple
                ? "Tap to toggle images — selection persists as you browse folders."
                : "Browse folders or search to find an existing image."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[#5C5248] hover:text-[#C16542]" aria-label="Close" data-testid="media-picker-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="px-5 py-3 border-b border-[#2C2621]/10 flex flex-wrap items-center gap-3 bg-[#FDFBF7]">
          {path !== "/" && (
            <button
              type="button"
              onClick={() => { setFilter(""); setPath(parentOf(path)); }}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
              data-testid="media-picker-up"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Up
            </button>
          )}
          <nav className="flex items-center gap-1 text-sm">
            {crumbs.map((c, i) => (
              <span key={c.path} className="flex items-center gap-1">
                <button type="button" onClick={() => { setFilter(""); setPath(c.path); }} className={`hover:text-[#C16542] ${path === c.path ? "text-[#2C2621]" : "text-[#5C5248]"}`}>
                  {c.label}
                </button>
                {i < crumbs.length - 1 && <span className="text-[#5C5248]/50">/</span>}
              </span>
            ))}
          </nav>
          <div className="flex-1" />
          {multiple && selectedMap.size > 0 && (
            <button
              type="button"
              onClick={clearMulti}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
              data-testid="media-picker-clear"
            >
              <X className="w-3.5 h-3.5" /> Clear ({selectedMap.size})
            </button>
          )}
          <label className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5248]" strokeWidth={1.6} />
            <input
              type="search"
              placeholder="Search all images…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-72 max-w-full pl-9 pr-3 py-2 text-sm border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542] bg-white"
              data-testid="media-picker-search"
            />
          </label>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && <p className="text-sm text-[#C16542] mb-3" data-testid="media-picker-error">{error}</p>}
          {!filter && children.length > 0 && (
            <>
              <p className="overline text-[#5C5248] mb-3">Folders</p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 mb-6">
                {children.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => setPath(f.path)}
                    className="flex items-center gap-3 px-3 py-3 border border-[#2C2621]/10 hover:border-[#C16542]/60 transition-colors bg-white"
                    data-testid={`media-picker-folder-${f.path}`}
                  >
                    <Folder className="w-4 h-4 text-[#A07042]" strokeWidth={1.6} />
                    <span className="text-sm truncate flex-1 text-left">{f.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-[#5C5248]" />
                  </button>
                ))}
              </div>
            </>
          )}

          <p className="overline text-[#5C5248] mb-3">
            {filter ? `Search results · ${assets.length}` : `Images · ${assets.length}`}
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-[#5C5248]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : assets.length === 0 ? (
            <div className="border border-dashed border-[#2C2621]/20 py-12 text-center text-[#5C5248]">
              <ImageIcon className="w-7 h-7 mx-auto mb-3" strokeWidth={1.4} />
              <p className="font-serif-x text-lg text-[#2C2621]">No images here</p>
              <p className="mt-1 text-sm">Upload in <a href="/admin/media" target="_blank" rel="noopener" className="underline text-[#C16542]">Media Library</a> first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3" data-testid="media-picker-grid">
              {assets.map((a) => {
                const isSel = multiple ? selectedMap.has(a.id) : selectedId === a.id;
                const handleClick = () => {
                  if (multiple) toggleMulti(a);
                  else setSelectedId(a.id);
                };
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={handleClick}
                    onDoubleClick={() => {
                      if (multiple) {
                        toggleMulti(a);
                      } else if (onSelect) {
                        onSelect(a);
                      }
                    }}
                    className={`group relative text-left bg-white overflow-hidden border-2 transition-colors ${isSel ? "border-[#C16542]" : "border-[#2C2621]/10 hover:border-[#C16542]/60"}`}
                    data-testid={`media-picker-asset-${a.id}`}
                    aria-pressed={isSel}
                  >
                    {multiple && (
                      <span
                        className={`absolute top-2 left-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-sm shadow-sm border ${
                          isSel
                            ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542]"
                            : "bg-white/95 text-[#2C2621] border-[#2C2621]/20"
                        }`}
                        aria-hidden="true"
                      >
                        {isSel ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={1.8} />}
                      </span>
                    )}
                    <div className="aspect-square overflow-hidden bg-[#F2EBE1] relative">
                      <span aria-hidden="true" className="picker-thumb-skel absolute inset-0 pointer-events-none" />
                      <img
                        src={resolveUrl(a.url)}
                        alt={a.title || a.filename}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        onLoad={(e) => e.currentTarget.previousSibling && e.currentTarget.previousSibling.classList.add('picker-thumb-skel--done')}
                        className="w-full h-full object-cover relative z-[1]"
                      />
                    </div>
                    <div className="px-2.5 py-2">
                      <p className="text-xs truncate text-[#2C2621]" title={a.filename}>{a.title || a.filename}</p>
                      <p className="text-[9px] tracking-[0.18em] uppercase text-[#5C5248]/80 truncate">{a.folder}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t border-[#2C2621]/10 px-5 py-4 flex flex-wrap items-center justify-between gap-3 bg-[#FDFBF7]">
          <p className="text-xs text-[#5C5248] truncate" data-testid="media-picker-status">
            {multiple
              ? selectedMap.size > 0
                ? `${selectedMap.size} image${selectedMap.size > 1 ? "s" : ""} selected`
                : "Tap one or more images to select."
              : selected
                ? `Selected: ${selected.title || selected.filename}`
                : "Pick an image to continue. Double-click also selects."}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase">
              Cancel
            </button>
            <button
              type="button"
              disabled={multiple ? selectedMap.size === 0 : !selected}
              onClick={multiple ? confirmMulti : confirmSingle}
              className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-40 text-[#FDFBF7] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase"
              data-testid="media-picker-confirm"
            >
              {finalConfirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
